import React, { useState, useEffect } from 'react';
import { getAdminDirectionUsers, getMembres, getAvailableModules, updateAdminModules, promoteToAdminDirection, demoteAdminDirection, getParametrage } from '../services/api';
import { FiShield, FiPlus, FiEdit2, FiTrash2, FiX, FiSearch } from 'react-icons/fi';

const AdminDirections = () => {
  const [admins, setAdmins] = useState<any[]>([]);
  const [modules, setModules] = useState<any[]>([]);
  const [directions, setDirections] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Modal for editing modules
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editModules, setEditModules] = useState<any>({});

  // Modal for promoting a new admin_direction
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [searchMembre, setSearchMembre] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [promoteModules, setPromoteModules] = useState<any>({});

  const loadData = () => {
    getAdminDirectionUsers().then(r => setAdmins(r.data)).catch(() => {});
    getAvailableModules().then(r => setModules(r.data)).catch(() => {});
    getParametrage('directions').then(r => setDirections(r.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const flash = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
    else { setError(msg); setTimeout(() => setError(''), 3000); }
  };

  // Search membres for promotion
  const handleSearchMembre = () => {
    if (!searchMembre.trim()) return;
    getMembres({ q: searchMembre, limit: 20 }).then(r => {
      // Filter out existing admin_direction and superadmin
      setSearchResults(r.data.data.filter((m: any) => !['superadmin', 'admin_direction'].includes(m.role)));
    }).catch(() => {});
  };

  const openPromote = () => {
    setShowPromoteModal(true);
    setSelectedUser(null);
    setSearchMembre('');
    setSearchResults([]);
    const initial: any = {};
    modules.forEach(m => { initial[m.key] = { can_read: true, can_write: false }; });
    setPromoteModules(initial);
  };

  const handlePromote = async () => {
    if (!selectedUser) return;
    try {
      const mods = Object.entries(promoteModules)
        .filter(([_, v]: any) => v.can_read)
        .map(([key, v]: any) => ({ module: key, can_read: true, can_write: v.can_write }));
      await promoteToAdminDirection(selectedUser.id, mods);
      flash('Utilisateur promu admin direction', 'success');
      setShowPromoteModal(false);
      loadData();
    } catch (err: any) {
      flash(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const openEdit = (admin: any) => {
    setEditingUser(admin);
    const current: any = {};
    modules.forEach(m => {
      const existing = (admin.modules || []).find((am: any) => am.module === m.key);
      current[m.key] = existing ? { can_read: !!existing.can_read, can_write: !!existing.can_write } : { can_read: false, can_write: false };
    });
    setEditModules(current);
    setShowEditModal(true);
  };

  const handleUpdateModules = async () => {
    if (!editingUser) return;
    try {
      const mods = Object.entries(editModules)
        .filter(([_, v]: any) => v.can_read)
        .map(([key, v]: any) => ({ module: key, can_read: true, can_write: v.can_write }));
      await updateAdminModules(editingUser.id, mods);
      flash('Modules mis à jour', 'success');
      setShowEditModal(false);
      loadData();
    } catch (err: any) {
      flash(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const handleDemote = async (userId: string) => {
    if (!window.confirm('Retirer le rôle admin direction à cet utilisateur ?')) return;
    try {
      await demoteAdminDirection(userId);
      flash('Utilisateur rétrogradé en membre', 'success');
      loadData();
    } catch (err: any) {
      flash(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const toggleModule = (obj: any, setObj: any, key: string, field: string) => {
    const current = { ...obj };
    if (field === 'can_read' && current[key].can_read) {
      current[key] = { can_read: false, can_write: false };
    } else if (field === 'can_read') {
      current[key].can_read = true;
    } else {
      current[key].can_write = !current[key].can_write;
    }
    setObj(current);
  };

  const ModuleCheckboxes = ({ values, setValues }: { values: any; setValues: any }) => (
    <table className="data-table" style={{ marginTop: 8 }}>
      <thead>
        <tr><th>Module</th><th style={{ width: 80, textAlign: 'center' }}>Lecture</th><th style={{ width: 80, textAlign: 'center' }}>Écriture</th></tr>
      </thead>
      <tbody>
        {modules.map(m => (
          <tr key={m.key}>
            <td>{m.label}</td>
            <td style={{ textAlign: 'center' }}>
              <input type="checkbox" checked={values[m.key]?.can_read || false} onChange={() => toggleModule(values, setValues, m.key, 'can_read')} />
            </td>
            <td style={{ textAlign: 'center' }}>
              <input type="checkbox" checked={values[m.key]?.can_write || false} disabled={!values[m.key]?.can_read} onChange={() => toggleModule(values, setValues, m.key, 'can_write')} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div>
      <h1 className="page-title">Gestion des Admins Direction</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <span>Administrateurs par direction ({admins.length})</span>
          <button className="btn btn-success" onClick={openPromote}><FiPlus /> Désigner un admin direction</button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Nom</th><th>Direction</th><th>Email</th><th>Modules autorisés</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {admins.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20 }}>Aucun admin direction défini</td></tr>
                )}
                {admins.map(a => (
                  <tr key={a.id}>
                    <td><strong>{a.prenomnom}</strong><br /><small>{a.matricule}</small></td>
                    <td><span className="badge badge-blue">{a.direction || '-'}</span></td>
                    <td>{a.email || '-'}</td>
                    <td>
                      {(a.modules || []).filter((m: any) => m.can_read).map((m: any) => (
                        <span key={m.module} className="badge badge-green" style={{ marginRight: 4, marginBottom: 2 }}>
                          {m.module}{m.can_write ? ' (R/W)' : ' (R)'}
                        </span>
                      ))}
                      {(!a.modules || a.modules.length === 0) && <span style={{ color: '#999' }}>Aucun module</span>}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-warning" onClick={() => openEdit(a)} title="Modifier les modules"><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDemote(a.id)} title="Retirer le rôle"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modules Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3>Modules de {editingUser.prenomnom}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p><strong>Direction :</strong> {editingUser.direction || '-'}</p>
              <ModuleCheckboxes values={editModules} setValues={setEditModules} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleUpdateModules}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Promote Modal */}
      {showPromoteModal && (
        <div className="modal-overlay" onClick={() => setShowPromoteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>Désigner un admin direction</h3>
              <button className="modal-close" onClick={() => setShowPromoteModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {!selectedUser ? (
                <>
                  <p>Rechercher un membre à promouvoir :</p>
                  <div className="search-box" style={{ marginBottom: 12 }}>
                    <FiSearch className="search-icon" />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Nom, matricule, email..."
                      value={searchMembre}
                      onChange={e => setSearchMembre(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearchMembre()}
                    />
                    <button className="btn btn-primary" onClick={handleSearchMembre} style={{ marginLeft: 8 }}>Chercher</button>
                  </div>
                  {searchResults.length > 0 && (
                    <table className="data-table">
                      <thead><tr><th>Nom</th><th>Direction</th><th>Rôle</th><th></th></tr></thead>
                      <tbody>
                        {searchResults.map(m => (
                          <tr key={m.id}>
                            <td>{m.prenomnom}<br /><small>{m.matricule}</small></td>
                            <td>{m.direction || '-'}</td>
                            <td><span className="badge badge-blue">{m.role}</span></td>
                            <td><button className="btn btn-success btn-sm" onClick={() => setSelectedUser(m)}>Choisir</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <>
                  <div style={{ background: '#f0f7ff', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <strong>{selectedUser.prenomnom}</strong> — {selectedUser.direction || 'Sans direction'}
                    <button className="btn btn-outline btn-sm" style={{ marginLeft: 12 }} onClick={() => setSelectedUser(null)}>Changer</button>
                  </div>
                  <p><strong>Modules à autoriser :</strong></p>
                  <ModuleCheckboxes values={promoteModules} setValues={setPromoteModules} />
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPromoteModal(false)}>Annuler</button>
              {selectedUser && <button className="btn btn-primary" onClick={handlePromote}><FiShield /> Promouvoir</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDirections;
